<template>
  <v-row
    v-if="categories && categories.length"
    class="mb-15 mt-2 justify-center align-center"
    v-resize="onResize"
  >
    <v-col cols="12" class="text-center">
      <h1 class="text-h5 text-md-h3">Utforsk nettbutikken nå!</h1>
    </v-col>
    <v-col cols="6" sm="3" class="pa-2" v-for="(category, index) in categories">
      <v-hover v-slot="{ hover }" :key="index">
        <v-card
          class="mx-auto"
          color="grey lighten-4"
          @click="goToCategory(category)"
        >
          <v-img
            :src="category.image.CropUrl"
            contain
            lazy-src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mP8/OlTCAAI5AMtwFOAFgAAAABJRU5ErkJggg=="
          >
            <v-expand-transition>
              <div
                class="category-overlay"
                style="height: 100%; background-color: transparent"
              >
                <div
                  class="category__tile--hover transition-fast-in-fast-out d-flex justify-center align-center v-card--reveal white--text text-center"
                  :style="{
                    height: `${
                      hover ? '100%' : supportsHover ? '30%' : '40%'
                    }`,
                  }"
                ></div>
                <div
                  class="transition-fast-in-fast-out d-flex justify-center align-center v-card--reveal white--text text-center"
                  :style="{
                    height: `${
                      hover ? '100%' : supportsHover ? '30%' : '40%'
                    }`,
                  }"
                >
                  <span :class="hover ? 'text-h4' : 'text-h5'">
                    {{ category.title }}
                  </span>
                </div>
              </div>
            </v-expand-transition>
          </v-img>
        </v-card>
      </v-hover>
    </v-col>
  </v-row>
</template>

<script>
import webshopMixins from "@/components/webshop/cart/webshop.mixins.js";
import { waitFor } from "vue-wait";
import { debounce } from "lodash-es";

export default {
  mixins: [webshopMixins],
  data: () => ({
    windowHeight: window.innerHeight,
    windowSize: { x: window.innerWidth, y: window.innerHeight },
    showAll: false,
    searchSelectedProduct: null,
    searchText: null,
    supportsHover: !("ontouchstart" in window || navigator.maxTouchPoints),
    search: false,
  }),
  methods: {
    setWindowHeight() {
      this.windowHeight = window.innerHeight;
    },
    onResize() {
      this.supportsHover = !(
        "ontouchstart" in window || navigator.maxTouchPoints
      );
      this.windowSize = { x: window.innerWidth, y: window.innerHeight };
    },
    executeSearch: debounce(function(string) {
      this.showAll = false;
      waitFor(`loading webshop getProductsBySearchText`, async function() {
        await this.$store.dispatch("webshop/getProductsBySearchText", {
          chainId: this.$route.params.chainId,
          searchText: string,
        });
      }).apply(this);
    }, 300),

    goToProductDetails(product) {
      this.closeSearch();
      this.$router
        .push({
          name: "webshopChainProductDetails",
          params: {
            productId: product.idBase64,
            productTitle: product.urlSafeTitle || "product",
          },
        })
        .catch(() => {});
    },

    goToHome() {
      this.$router
        .push({
          name: "webshopChain",
          params: {
            chainId: this.$route.params.chainId,
          },
        })
        .catch(() => {});
    },

    activateTabCategory() {
      this.$nextTick(() => {
        const tab = this.$refs["tab-" + this.$route.query.category];
        if (!this.searchText && tab && tab.length) tab[0].toggle();
      });
    },

    searchBlurEvent() {
      this.search = this.searchText && this.searchText.length > 1;
      this.activateTabCategory();
    },

    searchGoToCategory(category) {
      let mainCategory = this.categories.find((x) =>
        x.children.map((x) => x.title).includes(category.title)
      );
      this.closeSearch();
      this.$router
        .push({
          name: "webshopChainProductsMainCategories",
          params: { categoryFirst: mainCategory.urlSafeTitle },
          query: {
            [category.webshopCategoryId ? "category" : "mCategory"]:
              category.webshopCategoryId || category.urlSafeName,
          },
        })
        .catch(() => {});
    },

    searchGoToBrandSerie(brandSerie) {
      this.closeSearch();
      this.$router
        .push({
          name: "webshopChainProductsCategories",
          query: {
            serie: [brandSerie.id.Base64Guid],
            brand: [brandSerie.brandId.Base64Guid],
          },
        })
        .catch(() => {});
    },

    async goToCategory(category) {
      this.$eventBus.$emit("webshop-main-category-update", true);
      this.closeSearch();
      await this.$router
        .push({
          name: "webshopChainProductsMainCategories",
          params: { categoryFirst: category.urlSafeTitle },
          query: null,
        })
        .catch(() => {});
      this.$eventBus.$emit("webshop-main-category-update", false);
    },

    closeSearch() {
      if (this.search && this.searchText) {
        this.showAll = false;
        this.search = false;
        this.searchText = null;
      }
    },
  },
  computed: {
    maxSearchContainerHeight() {
      return this.$vuetify.breakpoint.mdAndUp ? 500 : this.windowHeight - 176;
    },
    searchProductsCategories() {
      return this.$store.getters["webshop/get"](
        "searchProductsCategories"
      ).filter((x) => x.count);
    },

    searchProductsBrands() {
      return this.$store.getters["webshop/get"]("searchProductsBrands");
    },

    productsSearchFilter() {
      return this.$store.getters["webshop/get"]("searchProducts");
    },

    productsSearchFilterLimit() {
      if (this.showAll || this.$vuetify.breakpoint.mdAndUp)
        return this.$store.getters["webshop/get"]("searchProducts");
      else
        return this.$store.getters["webshop/get"]("searchProducts").slice(0, 5);
    },

    activeTab() {
      return (
        this.categories.findIndex(
          (c) => this.$route.params.categoryFirst === c.urlSafeTitle
        ) + 1
      ); // to account for home tab
    },

    categoriesFiltered() {
      return this.mappedCategories.filter((x) => x.count > 0);
    },

    brandsAndSeriesFiltered() {
      let array = [];
      this.searchProductsBrands.forEach((x) => {
        let children = x.children.map(
          (y) =>
            new Object({
              ...y,
              count: this.productsSearchFilter
                .map((x) => x.serieName)
                .filter((x) => x === y.title).length,
              titleBrand: x.title,
              brandId: x.id,
            })
        );
        array.push(children);
      });
      return array.flat().filter((x) => x.count > 0);
    },
  },
  watch: {
    searchText(val) {
      let html = document.getElementsByTagName("HTML")[0];
      html.style.overflow = val ? "hidden" : "auto";
      this.$emit("overlay-event", val && val.length > 1);
      if (val && val.length > 1) {
        this.executeSearch(val);
      }
    },

    "$route.query.category": {
      handler(val) {
        if (parseInt(val) >= 0)
          this.$nextTick(() => {
            let tab = this.$refs["tab-" + val];
            if (tab && tab.length) tab[0].toggle();
          });
      },
      immediate: true,
    },
  },
};
</script>

<style lang="scss" scoped>
.category__tile--hover {
  background: var(--v-primary-base) !important;
  opacity: 0.8;
}
.v-card--reveal {
  align-items: center;
  bottom: 0;
  justify-content: center;
  position: absolute;
  width: 100%;
}
</style>
<style lang="scss">
.display-1 {
  font-family: "niveau-grotesk" !important;
}
</style>
